import type * as React from 'react';
import { useCallback, useRef } from 'react';

import { trackPatientSearched } from '@/models/analytics/features/trackPatientSearchAnalytics';
import {
  SEARCH_PATIENT_PROFILES,
  useSearchPatientProfiles as useSearchPatientProfilesCore,
  type PatientSearchedMeasurements,
  type UseSearchPatientProfilesResult,
} from '@heidi/patients/data';

/**
 * RE-EXPORT, NEVER A RE-DECLARATION. The literal now lives once, in
 * `@heidi/patients/contracts` — this line keeps the old import path working
 * without giving the cache a second declaration site (`key-single-site`).
 */
export { SEARCH_PATIENT_PROFILES };

interface UseSearchPatientProfilesArgs {
  query: string;
  limit?: number;
  enabled?: boolean;
  /**
   * Scopes the BE search to the patient-profile owner resolved from this
   * session. Required for the assistant-acting-for-clinician case —
   * without it, the BE treats the caller as searching their own list and
   * assistants get zero results regardless of what they type (DEV-12028).
   * Undefined falls back to owner-mode (the caller's own profiles), which
   * is correct when there is no session context.
   */
  sessionId?: string;
  /**
   * Funnel-tracking context from the controller. When provided, the hook
   * fires `patient_searched` once per BE response (queryFn invocation —
   * cache hits skip), carrying `query_length` (never the raw text — PII),
   * `result_count`, `has_zero_results`, `latency_ms`, `session_id`, the
   * current `search_id`, and `feature_flag_variant`. Optional — without
   * it the event does not fire (other consumers of this hook that don't
   * participate in the funnel won't emit it).
   */
  tracking?: {
    sessionId: string | undefined;
    searchIdRef: React.MutableRefObject<string | undefined>;
    featureFlagVariant: 'treated' | 'control' | 'unknown';
  };
}

/**
 * App-side delegate for `@heidi/patients/data` (ARCFE-1057).
 *
 * The query — debounce, 2-char gate, `keepPreviousData`, the 30 s stale
 * window, the clear-on-empty effect — moved into the package. What stayed is
 * the one thing that cannot go there: the `patient_searched` emit.
 * `@heidi/patients/data` is headless, so the event is an app reaction fired
 * from the package's `onSearched` port at the exact position the legacy
 * dispatch fired (inside `queryFn`, after the response, before the return).
 *
 * THE EMIT LEFT THE REMATCH BUS at openspec task 2.1b. It was
 * `dispatch.analyticsScribe.trackPatientSearched`; it is now the app-side host
 * `trackPatientSearched`, which calls `track()` with the same
 * `{ amplitude: false }` the wrapper passed. `trackV2` is a pure alias of
 * `track`, so the destination set is unchanged — what changed is that this
 * module no longer imports `@/store` or `react-redux`, which shrinks the
 * Rematch ratchet in all three of its dimensions at once.
 *
 * The payload is assembled by spreading the package's four measurements ahead
 * of the three app-context fields, which reproduces the legacy key ORDER as
 * well as the legacy key SET.
 *
 * `trackingRef` keeps the pre-carve semantics that the event reads the LATEST
 * tracking context at fire time rather than the one captured when the fetch
 * started — and, as before, `tracking` never enters the queryKey, so a new
 * `search_id` (minted per focus) cannot invalidate the cached result for a
 * query string the user is still typing.
 */
export function useSearchPatientProfiles({
  query,
  limit,
  enabled,
  sessionId,
  tracking,
}: UseSearchPatientProfilesArgs): UseSearchPatientProfilesResult {
  const trackingRef = useRef(tracking);
  trackingRef.current = tracking;

  const onSearched = useCallback(
    (measurements: PatientSearchedMeasurements) => {
      const currentTracking = trackingRef.current;
      // The gate the legacy body had: no tracking context, no event.
      if (!currentTracking) return;
      trackPatientSearched({
        ...measurements,
        session_id: currentTracking.sessionId,
        search_id: currentTracking.searchIdRef.current,
        feature_flag_variant: currentTracking.featureFlagVariant,
      });
    },
    // The host is a module binding, so this callback is stable for the life of
    // the component — as it already was via `[dispatch]`. `tracking` is still
    // read through `trackingRef` rather than closed over, which is what keeps a
    // new `search_id` out of the queryKey.
    []
  );

  return useSearchPatientProfilesCore({
    query,
    limit,
    enabled,
    sessionId,
    onSearched,
  });
}
